import React from 'react'
import styled from 'styled-components'
import { useKey } from 'react-use'

import { CheckboxInput, CheckmarkIcon, Dropdown } from '@sketch/components'

import { useResponsiveDropdown } from 'components/ResponsiveDropdown'
import { useArtboardContext } from 'modules/shares/DetailView/ArtboardContext'

import { MAX_ZOOM, MIN_ZOOM } from './constants'
import { ZoomTypes } from './types'

import { ToggleButton, Item, ItemLabel, ItemShortcut } from './Zoom.styles'
import { keyWithoutModifier, keyCodeWithShift } from '@sketch/utils'
import { IS_EMBEDDED } from '@sketch/constants'

// Preset zoom levels: 1% to 1000%
const zoomRange = [0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1.0, 2.0, 4.0, 8.0, 10]

const formatZoom = (zoom: ZoomTypes) =>
  `${Math.round((zoom.factor ?? 1) * 100)}%`

type ZoomProps = {
  className?: string
  zoom: ZoomTypes
  setZoom: (newZoom: ZoomTypes) => void
  minZoom?: number
  maxZoom?: number
}

interface ItemLabelCheckmarkProps {
  active?: boolean
}

const ItemLabelCheckmark: React.FC<ItemLabelCheckmarkProps> = props => (
  <>
    <CheckboxInput checked={props.active} variant="primary">
      {props.active && <CheckmarkIcon />}
    </CheckboxInput>
    <ItemLabel>{props.children}</ItemLabel>
  </>
)

interface ZoomDropdownProps {
  canZoomIn: boolean
  canZoomOut: boolean
  zoomIn: () => void
  zoomOut: () => void
  zoomToActualSize: () => void
  zoomToFit: () => void

  showGridOptions: boolean

  isGridEnabled?: boolean
  toggleGrid?: () => void

  isLayoutEnabled?: boolean
  toggleLayout?: () => void
}

const ZoomDropdown = (props: ZoomDropdownProps) => {
  return (
    <>
      <Item onClick={props.zoomIn} disabled={!props.canZoomIn}>
        <ItemLabel>Zoom In</ItemLabel>
        {!IS_EMBEDDED && <ItemShortcut>+</ItemShortcut>}
      </Item>

      <Item onClick={props.zoomOut} disabled={!props.canZoomOut}>
        <ItemLabel>Zoom Out</ItemLabel>
        {!IS_EMBEDDED && <ItemShortcut>-</ItemShortcut>}
      </Item>

      <Dropdown.Divider />

      <Item onClick={props.zoomToActualSize}>
        <ItemLabel>Actual Size</ItemLabel>
        {!IS_EMBEDDED && <ItemShortcut>Shift 0</ItemShortcut>}
      </Item>

      <Item onClick={props.zoomToFit}>
        <ItemLabel>Fit Canvas</ItemLabel>
        {!IS_EMBEDDED && <ItemShortcut>Shift 1</ItemShortcut>}
      </Item>

      {props.showGridOptions && (
        <>
          <Dropdown.Divider />
          <Item onClick={props.toggleGrid}>
            <ItemLabelCheckmark active={props.isGridEnabled}>
              Show Grid
            </ItemLabelCheckmark>
            <ItemShortcut>Shift G</ItemShortcut>
          </Item>
          <Item onClick={props.toggleLayout}>
            <ItemLabelCheckmark active={props.isLayoutEnabled}>
              Show Layout
            </ItemLabelCheckmark>
            <ItemShortcut>Shift H</ItemShortcut>
          </Item>
        </>
      )}
    </>
  )
}

const Zoom: React.FC<ZoomProps> = ({
  className,
  zoom,
  setZoom,
  minZoom = MIN_ZOOM,
  maxZoom = MAX_ZOOM,
}) => {
  const {
    isInspectorEnabled,
    toggleGrid,
    isGridEnabled,
    toggleLayout,
    isLayoutEnabled,
  } = useArtboardContext()

  // restrict zoom factors to [min, max] range
  const zoomFactors = [
    minZoom,
    ...zoomRange.filter(f => f > minZoom && f < maxZoom),
    maxZoom,
  ]

  const zoomIn = () => {
    // get current zoom level
    const current = zoom.factor ?? 1
    // find zoom value immediately above the current value
    const update = zoomFactors.find(z => z > current) ?? current
    // update zoom
    setZoom({ type: 'ZOOM_FACTOR', factor: update })
  }

  const zoomOut = () => {
    // get current zoom level
    const current = zoom.factor ?? 1
    // find zoom value immediately below the current value
    const update = zoomFactors.reverse().find(z => z < current) ?? current
    // update zoom
    setZoom({ type: 'ZOOM_FACTOR', factor: update })
  }

  const zoomToActualSize = () => setZoom({ type: 'ZOOM_FACTOR', factor: 1 })

  const zoomToFit = () => setZoom({ type: 'ZOOM_TO_FIT' })

  const canZoomIn = (zoom.factor ?? 1) < maxZoom
  const canZoomOut = (zoom.factor ?? 1) > minZoom

  useKey(keyWithoutModifier(['=', '+']), zoomIn)
  useKey(keyWithoutModifier('-'), zoomOut)
  useKey(keyCodeWithShift('Digit0'), zoomToActualSize)
  useKey(keyCodeWithShift('Digit1'), zoomToFit)

  const enableGridAndCanvasOptions =
    isInspectorEnabled && !window.location.pathname.includes('/canvas')

  useKey(keyCodeWithShift('KeyG'), () => {
    if (enableGridAndCanvasOptions) {
      toggleGrid()
    }
  })
  useKey(keyCodeWithShift('KeyH'), () => {
    if (enableGridAndCanvasOptions) {
      toggleLayout()
    }
  })

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ZoomDropdown,
    dropdownProps: {
      canZoomIn,
      canZoomOut,
      zoomIn,
      zoomOut,
      zoomToActualSize,
      zoomToFit,

      /* Props related with the grid layout options */
      showGridOptions: enableGridAndCanvasOptions,
      toggleGrid,
      toggleLayout,
      isGridEnabled,
      isLayoutEnabled,
    },
    placement: 'bottom-end',
  })

  return (
    <>
      <ToggleButton
        className={className}
        variant="strong"
        label={formatZoom(zoom)}
        fullWidth
        data-testid="zoom-toggle"
        chevrons
        {...buttonProps}
      />
      {content}
    </>
  )
}

const StyledZoom = styled(Zoom)``

export { StyledZoom as Zoom }
